import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { TranslateService, Translation } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';

@Injectable({
  providedIn: 'root',
})
export class TranslationStateService {
  private translateService = inject(TranslateService);
  private primeNg = inject(PrimeNG);

  locale = computed(() => this.language());

  private STORAGE_KEY: string = 'language' as const;
  private SUPPORTED_LANGUAGES: string[] = ['pt-BR'] as const;

  private language = signal<string>(
    (localStorage.getItem(this.STORAGE_KEY) as string) ?? 'pt-BR'
  );

  private cachedTranslations: Record<string, Translation> = {};

  constructor() {
    this.translateService.addLangs([...this.SUPPORTED_LANGUAGES]);

    effect(() => {
      const language = this.language();
      localStorage.setItem(this.STORAGE_KEY, language);

      this.translateService.use(language);
      this.loadPrimeNgLocaleAsync(language);
    });
  }

  setLanguage(lang: string) {
    if (this.SUPPORTED_LANGUAGES.includes(lang)) this.language.set(lang);
  }

  async loadPrimeNgLocaleAsync(lang: string): Promise<void> {
    try {
      if (this.cachedTranslations[lang]) {
        this.primeNg.setTranslation(this.cachedTranslations[lang]);
        return;
      }

      const module = await import(
        `../../assets/i18n/primeng/primeng-${lang}.json`
      );
      if (module.default) {
        this.cachedTranslations[lang] = module.default;
        this.primeNg.setTranslation(module.default);
      }
    } catch (e) {
      console.error(`Could not load PrimeNG locale for ${lang}`, e);
    }
  }
}
